import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, User, Share2, Linkedin, Twitter, Facebook, Shield, Cpu, Server, Lock, Zap, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import DemoScheduler from "@/components/DemoScheduler";
import SEO from "@/components/SEO";
import openclawImage from "@/assets/openclaw_mascote_crud.png";

const BlogPostOpenClaw = () => {
    const { isOpen, setIsOpen } = useBlogContactPopup(3000);

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="OpenClaw e ITSM: A Revolução da Automação Inteligente e Segura"
                description="Descubra como o OpenClaw, assistente de IA autônomo e de código aberto, está revolucionando o ITSM com hiperautomação local, resolução proativa de tickets e soberania de dados."
                canonical="https://aroan.com.br/blog/openclaw-itsm-automacao-inteligente"
                ogImage={openclawImage}
                author="Equipe Aroan"
                publishedTime="2025-02-18T00:00:00-03:00"
                keywords="OpenClaw, ITSM, automação inteligente, IA local, soberania de dados, hiperautomação, service desk, segurança de dados, Shadow AI"
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            IA e Automação
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
                            <span className="text-primary">OpenClaw</span> e ITSM: A Revolução da Automação Inteligente e Segura 🚀
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">Equipe Aroan</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span>18 Fev, 2025</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                <span>8 min de leitura</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Image */}
            <section className="pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in bg-card flex items-center justify-center">
                        <img
                            src={openclawImage}
                            alt="OpenClaw - Mascote da automação inteligente"
                            className="w-full h-auto object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

                            {/* Left Column */}
                            <div className="space-y-8">
                                {/* Introduction */}
                                <div className="prose prose-lg dark:prose-invert max-w-none">
                                    <h2 className="text-3xl font-bold mb-6 text-foreground">Introdução</h2>
                                    <p className="text-lg leading-relaxed">
                                        O universo da Gestão de Serviços de TI (ITSM) está à beira de uma transformação sem precedentes, impulsionada por uma inovação que promete redefinir os padrões de eficiência e segurança: o <strong>OpenClaw</strong>.
                                    </p>
                                    <p>
                                        Este assistente de Inteligência Artificial (IA) de código aberto e autônomo não é apenas mais uma ferramenta; ele representa um salto qualitativo na automação, especialmente para organizações que buscam otimizar seus processos de TI sem comprometer a integridade e a privacidade de seus dados.
                                    </p>
                                    <p>
                                        Tradicionalmente, a automação no ITSM tem sido limitada por soluções baseadas em regras ou por IAs dependentes da nuvem, que muitas vezes levantam preocupações sobre a soberania dos dados e a segurança. O OpenClaw surge como uma resposta a esses desafios.
                                    </p>
                                </div>

                                {/* Why OpenClaw is a Game Changer */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Cpu className="w-6 h-6 text-primary" />
                                        <h3 className="text-2xl font-bold text-foreground">Por Que o OpenClaw é um Game-Changer?</h3>
                                    </div>
                                    <p className="text-muted-foreground mb-4">
                                        O OpenClaw se destaca no cenário do ITSM por suas capacidades únicas que abordam diretamente as lacunas das soluções existentes.
                                    </p>
                                    <div className="space-y-4">
                                        <div className="border-l-4 border-primary pl-4">
                                            <h4 className="font-semibold text-foreground mb-2">1. Hiperautomação Local e Segura</h4>
                                            <p className="text-muted-foreground text-sm">
                                                Ao contrário de soluções de IA que operam em nuvem pública, o OpenClaw foi projetado para funcionar <strong>localmente</strong>, dentro da infraestrutura da sua empresa. Dados sensíveis permanecem protegidos dentro do perímetro da organização, garantindo conformidade com regulamentações rigorosas de privacidade.
                                            </p>
                                        </div>
                                        <div className="border-l-4 border-green-500 pl-4">
                                            <h4 className="font-semibold text-foreground mb-2">2. Resolução Proativa de Tickets</h4>
                                            <p className="text-muted-foreground text-sm">
                                                Atuando como um "agente de nível 0" autônomo, ele monitora continuamente os sistemas, identifica anomalias e executa ações corretivas de forma proativa, minimizando interrupções e aumentando a disponibilidade dos serviços.
                                            </p>
                                        </div>
                                        <div className="border-l-4 border-blue-500 pl-4">
                                            <h4 className="font-semibold text-foreground mb-2">3. Soberania de Dados</h4>
                                            <p className="text-muted-foreground text-sm">
                                                Ao operar localmente, ele permite que as empresas utilizem o poder da IA avançada sem a necessidade de expor informações corporativas sensíveis a serviços externos ou provedores de nuvem.
                                            </p>
                                        </div>
                                        <div className="border-l-4 border-orange-500 pl-4">
                                            <h4 className="font-semibold text-foreground mb-2">4. Agente de Campo Digital</h4>
                                            <p className="text-muted-foreground text-sm">
                                                Pode atuar como um verdadeiro "agente de campo digital", executando tarefas técnicas complexas como implantações de software, configurações de rede, auditorias de segurança e manutenção de sistemas.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Shadow AI Section */}
                                <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Eye className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                                        <h3 className="text-2xl font-bold text-amber-800 dark:text-amber-200">Da "Shadow AI" à Automação Controlada</h3>
                                    </div>
                                    <p className="text-amber-700 dark:text-amber-300 mb-4">
                                        O OpenClaw representa a transição crucial de IAs que apenas conversam para IAs que realmente <strong>fazem</strong>. Ele é a resposta eficaz para a crescente preocupação com a "Shadow AI" – o uso não oficial e não gerenciado de ferramentas de IA por funcionários.
                                    </p>
                                    <p className="text-amber-700 dark:text-amber-300">
                                        Ao oferecer uma alternativa controlada, de código aberto e local para a automação inteligente, o OpenClaw permite que as empresas abracem a inovação da IA com confiança e governança.
                                    </p>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-8">
                                {/* Comparison Table */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Server className="w-6 h-6 text-primary" />
                                        <h3 className="text-2xl font-bold text-foreground">IA na Nuvem vs. OpenClaw (IA Local)</h3>
                                    </div>
                                    <p className="text-muted-foreground mb-6">
                                        Para entender o verdadeiro valor do OpenClaw, é fundamental comparar sua abordagem com as soluções de IA baseadas em nuvem.
                                    </p>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-border">
                                                    <th className="text-left py-3 px-2 font-semibold text-foreground">Característica</th>
                                                    <th className="text-center py-3 px-2 font-semibold text-muted-foreground">IA Nuvem</th>
                                                    <th className="text-center py-3 px-2 font-semibold text-primary">OpenClaw</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="border-b border-border/50">
                                                    <td className="py-3 px-2 font-medium text-foreground">Local de Operação</td>
                                                    <td className="py-3 px-2 text-center text-muted-foreground">Servidores de terceiros</td>
                                                    <td className="py-3 px-2 text-center text-primary font-semibold">Infraestrutura local</td>
                                                </tr>
                                                <tr className="border-b border-border/50">
                                                    <td className="py-3 px-2 font-medium text-foreground">Soberania de Dados</td>
                                                    <td className="py-3 px-2 text-center text-red-500">Dados processados externamente</td>
                                                    <td className="py-3 px-2 text-center text-green-600 dark:text-green-400 font-semibold">Controle total</td>
                                                </tr>
                                                <tr className="border-b border-border/50">
                                                    <td className="py-3 px-2 font-medium text-foreground">Segurança</td>
                                                    <td className="py-3 px-2 text-center text-muted-foreground">Depende do provedor</td>
                                                    <td className="py-3 px-2 text-center text-green-600 dark:text-green-400 font-semibold">Controlada internamente</td>
                                                </tr>
                                                <tr className="border-b border-border/50">
                                                    <td className="py-3 px-2 font-medium text-foreground">Latência</td>
                                                    <td className="py-3 px-2 text-center text-muted-foreground">Pode ser afetada</td>
                                                    <td className="py-3 px-2 text-center text-green-600 dark:text-green-400 font-semibold">Baixa, alta responsividade</td>
                                                </tr>
                                                <tr className="border-b border-border/50">
                                                    <td className="py-3 px-2 font-medium text-foreground">Customização</td>
                                                    <td className="py-3 px-2 text-center text-muted-foreground">Limitada</td>
                                                    <td className="py-3 px-2 text-center text-green-600 dark:text-green-400 font-semibold">Alta integração</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-2 font-medium text-foreground">Custo</td>
                                                    <td className="py-3 px-2 text-center text-muted-foreground">Por uso, escala rápido</td>
                                                    <td className="py-3 px-2 text-center text-green-600 dark:text-green-400 font-semibold">Custos previsíveis</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Key Benefits */}
                                <div className="space-y-4">
                                    <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                                        <div className="flex items-start gap-3 mb-3">
                                            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                                            <h4 className="font-semibold text-blue-800 dark:text-blue-200">Hiperautomação de TI</h4>
                                        </div>
                                        <p className="text-blue-700 dark:text-blue-300 text-sm">
                                            A hiperautomação significa a capacidade de automatizar processos complexos de ponta a ponta, com a IA agindo diretamente nos sistemas internos, sem depender de serviços externos.
                                        </p>
                                    </div>

                                    <div className="border border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50 dark:bg-green-900/20">
                                        <div className="flex items-start gap-3 mb-3">
                                            <Zap className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                                            <h4 className="font-semibold text-green-800 dark:text-green-200">Resolução Antes do Ticket</h4>
                                        </div>
                                        <p className="text-green-700 dark:text-green-300 text-sm">
                                            Imagine problemas de TI sendo resolvidos antes mesmo que os usuários percebam. O OpenClaw monitora continuamente, identifica anomalias e executa ações corretivas proativamente.
                                        </p>
                                    </div>

                                    <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/20">
                                        <div className="flex items-start gap-3 mb-3">
                                            <Lock className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" />
                                            <h4 className="font-semibold text-purple-800 dark:text-purple-200">Dados Sempre Protegidos</h4>
                                        </div>
                                        <p className="text-purple-700 dark:text-purple-300 text-sm">
                                            A soberania de dados é um fator decisivo para setores regulamentados. O OpenClaw garante que a empresa mantenha controle total sobre seus dados em todos os momentos.
                                        </p>
                                    </div>

                                    <div className="border border-orange-200 dark:border-orange-800 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20">
                                        <div className="flex items-start gap-3 mb-3">
                                            <Cpu className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
                                            <h4 className="font-semibold text-orange-800 dark:text-orange-200">Agente de Campo Digital</h4>
                                        </div>
                                        <p className="text-orange-700 dark:text-orange-300 text-sm">
                                            O OpenClaw transcende o papel de um simples chatbot. Ele executa implantações de software, configurações de rede, auditorias de segurança e manutenção de sistemas autonomamente.
                                        </p>
                                    </div>
                                </div>

                                {/* Conclusion */}
                                <div className="bg-gradient-to-br from-primary/5 to-background border border-primary/10 rounded-2xl p-6">
                                    <h3 className="text-2xl font-bold mb-4 text-foreground">Conclusão: O Futuro do ITSM é Autônomo e Seguro</h3>
                                    <p className="text-muted-foreground mb-4">
                                        O OpenClaw não é apenas uma ferramenta; é uma filosofia de automação que prioriza a segurança, a soberania dos dados e a eficiência proativa.
                                    </p>
                                    <p className="text-muted-foreground">
                                        Ao capacitar as equipes de TI com um assistente de IA autônomo que opera localmente, ele abre caminho para um ITSM mais resiliente, responsivo e seguro. Sua empresa está pronta para abraçar o futuro do ITSM com automação segura e inteligente?
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Share Section */}
                        <div className="flex items-center justify-between border-y border-border py-6 mb-12 mt-12">
                            <span className="font-semibold">Compartilhe este artigo:</span>
                            <div className="flex gap-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-600"
                                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Linkedin className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-400"
                                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("OpenClaw e ITSM: A Revolução da Automação Inteligente e Segura")}`, '_blank')}
                                >
                                    <Twitter className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-700"
                                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Facebook className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                        if (navigator.share) {
                                            try {
                                                await navigator.share({
                                                    title: 'OpenClaw e ITSM: A Revolução da Automação Inteligente e Segura',
                                                    text: 'Descubra como o OpenClaw está revolucionando o ITSM!',
                                                    url: window.location.href,
                                                });
                                            } catch (err) {
                                                console.error('Error sharing:', err);
                                            }
                                        } else {
                                            navigator.clipboard.writeText(window.location.href);
                                            alert("Link copiado para a área de transferência!");
                                        }
                                    }}
                                >
                                    <Share2 className="w-5 h-5" />
                                </Button>
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="bg-foreground text-background rounded-2xl p-8 sm:p-12 text-center">
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                                Quer transformar sua operação de TI com automação inteligente?
                            </h2>
                            <p className="text-background/80 mb-8 max-w-2xl mx-auto">
                                Descubra como a AROAN pode unificar seus processos de ITSM com inteligência artificial proprietária, combinando o melhor da automação local e da nuvem.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button size="lg" variant="secondary" className="group" onClick={() => window.open('/#demo', '_self')}>
                                    Agende uma Demonstração
                                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                                <Button size="lg" variant="outline" className="bg-transparent border-background/20 text-background hover:bg-background/10" onClick={() => window.open('/materiais', '_self')}>
                                    Baixe o Guia Gratuito
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />

            <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
        </div>
    );
};

export default BlogPostOpenClaw;
